import { useState, useEffect } from 'react';
import {
  useTranslation,
  Theme,
  IconStyle,
  AccountType,
  Drawer,
  Button,
  ButtonStyles,
  ButtonSize,
  ButtonWidth,
  useMasterData,
  AlertMessage,
} from '@nextgen-web-framework/all';
import type { AppDispatch, AppState } from '@nextgen-web-framework/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  RegFlowConfig,
  updateCustomerType,
  updateRegistrationData,
  RegistrationBannerProps,
} from '@commerce-profile-webcomponents/sdk';
import { useLocale } from '../../utils/customHooks';
import RegistrationSelectAccountType from '../RegistrationSelectAccountType/RegistrationSelectAccountType';
import {
  fetchProfileCMSIfUndefined,
  fetchAccountTypeImagesCMSIfUndefined,
  fetchTermsNConditionsCMSIfUndefined,
  isLocaleDifferent,
} from '../../utils/ProfileCMSUtils';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import { getRegistrationFlow } from '../../utils/registrationUtils';
import styles from './RegistrationBanner.module.scss';

const RegistrationBanner = ({
  accountType,
  callback,
  sponsorNumber,
  sponsorName,
  fee,
  isEditable,
}: RegistrationBannerProps) => {
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const accountTypeImageCMSData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const tncCMSData = useSelector((state: AppState) => state.commerceProfile.termsNConditionsCMS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const siteLocale = useLocale();
  const config = useMasterData('RegistrationFlowConfigList');
  const apiErrorHandler = useGlobalErrorHandler();
  useEffect(() => {
    dispatch(updateCustomerType(accountType));

    //used for extracting the allowed account types
    const selfInitiatedFlow = getRegistrationFlow(config, RegFlowConfig.SelfInitiatedFlow);
    dispatch(
      updateRegistrationData({
        selectAccountType: {
          accountTypes: Object.keys(selfInitiatedFlow.steps['selectAccountTypeStep'].stepConfig.userType).sort(),
        },
      }),
    );
  }, [accountType, isEditable]);

  useEffect(() => {
    const sendAccountType = selectedAccountType ?? accountType;
    if (
      selectedAccountType !== accountType ||
      isLocaleDifferent(siteLocale, tncCMSData?.[selectedAccountType]?.locale)
    ) {
      fetchTermsNConditionsCMSIfUndefined(dispatch, tncCMSData, sendAccountType, siteLocale, apiErrorHandler);
    }
    if (selectedAccountType !== accountType) {
      fetchAccountTypeImagesCMSIfUndefined(dispatch, accountTypeImageCMSData, sendAccountType, apiErrorHandler);
    }
  }, [selectedAccountType, siteLocale]);

  useEffect(() => {
    fetchProfileCMSIfUndefined(dispatch, registrationCmsData, siteLocale, apiErrorHandler);
  }, [siteLocale]);

  const t = useTranslation();
  const openIconHandler = () => {
    setIsDrawerOpen(true);
  };
  const onClose = () => {
    setIsDrawerOpen(false);
  };
  const updateHandler = () => {
    onClose();
    callback(selectedAccountType);
  };
  const accountTypeLabel = t(`profile.common.selectAccountType.${accountType}`);

  const showDrawer = () => (
    <Drawer
      header={
        isEditable
          ? t('profile.RegistrationBanner.selectAccountType')
          : t('profile.RegistrationBanner.accountTypeRegistration', { accountType: accountTypeLabel })
      }
      closeIcon
      className="banner-drawer"
      onClose={onClose}
      bottomGap={0}
      data-testid="drawer-testid"
      footer={
        isEditable && (
          <Button
            data-testId="update-button-id"
            title={t('profile.RegistrationBanner.buttonTitle')}
            buttonStyle={ButtonStyles.SOLID}
            size={ButtonSize.MEDIUM}
            onClick={updateHandler}
            buttonWidth={ButtonWidth.FULL_WIDTH}
          />
        )
      }
    >
      <div data-testid="drawer-body">
        <RegistrationSelectAccountType isEditable={isEditable} isRegistrationBanner={true} />
      </div>
    </Drawer>
  );

  return (
    <div data-testid="registration-banner-container">
      <AlertMessage
        hasIcon={true}
        data-id="warning"
        icon={'id-card'}
        theme={Theme.ACTION}
        iconColor={Theme.ACTION}
        iconStyle={IconStyle.SOLID}
        className={styles['icon']}
        onClick={openIconHandler}
      >
        <div className={styles['banner-conatiner']} data-testid="banner-container-test-id">
          <div className={styles['banner-content']} data-testid="banner-content-test-id">
            {t('profile.RegistrationBanner.heading', { accountType: accountTypeLabel, fee: fee })}
          </div>

          <div className={styles['banner-details']} data-testid="banner-details-test-id">
            {t('profile.RegistrationBanner.description', {
              sponsorName: sponsorName,
              sponsorNumber: sponsorNumber,
            })}
          </div>
        </div>
      </AlertMessage>

      {isDrawerOpen && showDrawer()}
    </div>
  );
};
export default RegistrationBanner;
